/**
 * TurnDialogRenderer — turn confirmation modal with scale-punch entrance.
 *
 * Extracted from HUDRenderer as a composition class.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { PLAYER_COLORS, TURN_FLOW, WORLD, Z } from '../../constants';
import type { GameState } from '../../types';

export class TurnDialogRenderer {
  private dialogContainer: Container;
  private dialogBg: Graphics;
  private dialogBorder: Graphics;
  private dialogPlayerName: Text;
  private dialogPrompt: Text;
  private dialogInstruction: Text;
  private dialogShowTime: number = 0;

  constructor(uiContainer: Container) {
    const cx = WORLD.CANVAS_WIDTH / 2;
    const cy = WORLD.CANVAS_HEIGHT / 2;
    const dw = TURN_FLOW.DIALOG_WIDTH;
    const dh = TURN_FLOW.DIALOG_HEIGHT;

    this.dialogContainer = new Container();
    this.dialogContainer.position.set(cx, cy);
    this.dialogContainer.pivot.set(cx, cy);
    this.dialogContainer.zIndex = Z.HUD_DIALOG;
    this.dialogContainer.visible = false;
    uiContainer.addChild(this.dialogContainer);

    this.dialogBg = new Graphics();
    this.dialogBg.rect(cx - dw / 2, cy - dh / 2, dw, dh);
    this.dialogBg.fill({ color: TURN_FLOW.DIALOG_BG_COLOR, alpha: TURN_FLOW.DIALOG_BG_ALPHA });
    this.dialogBg.zIndex = Z.HUD_DIALOG;
    this.dialogContainer.addChild(this.dialogBg);

    this.dialogBorder = new Graphics();
    this.dialogBorder.rect(cx - dw / 2, cy - dh / 2, dw, dh);
    this.dialogBorder.stroke({ color: 0xffffff, width: TURN_FLOW.DIALOG_BORDER_WIDTH + 2 });
    this.dialogBorder.zIndex = Z.HUD_DIALOG_BORDER;
    this.dialogContainer.addChild(this.dialogBorder);

    this.dialogPlayerName = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: TURN_FLOW.DIALOG_NAME_FONT_SIZE,
        fontWeight: 'bold',
        fill: 0xffffff,
        stroke: { color: 0x000000, width: 3 },
      }),
    });
    this.dialogPlayerName.anchor.set(0.5, 0.5);
    this.dialogPlayerName.position.set(cx, cy - 35);
    this.dialogPlayerName.zIndex = Z.HUD_DIALOG_TEXT;
    this.dialogContainer.addChild(this.dialogPlayerName);

    this.dialogPrompt = new Text({
      text: 'NEXT TURN',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: TURN_FLOW.DIALOG_PROMPT_FONT_SIZE,
        fontWeight: 'bold',
        fill: 0xffffff,
        stroke: { color: 0x000000, width: 3 },
      }),
    });
    this.dialogPrompt.anchor.set(0.5, 0.5);
    this.dialogPrompt.position.set(cx, cy + 2);
    this.dialogPrompt.zIndex = Z.HUD_DIALOG_TEXT;
    this.dialogContainer.addChild(this.dialogPrompt);

    this.dialogInstruction = new Text({
      text: 'GET READY',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: TURN_FLOW.DIALOG_INSTRUCTION_FONT_SIZE,
        fill: 0x888888,
      }),
    });
    this.dialogInstruction.anchor.set(0.5, 0.5);
    this.dialogInstruction.position.set(cx, cy + 40);
    this.dialogInstruction.zIndex = Z.HUD_DIALOG_TEXT;
    this.dialogContainer.addChild(this.dialogInstruction);
  }

  show(playerIndex: number): void {
    const color = PLAYER_COLORS[playerIndex];
    const cx = WORLD.CANVAS_WIDTH / 2;
    const cy = WORLD.CANVAS_HEIGHT / 2;
    const dw = TURN_FLOW.DIALOG_WIDTH;
    const dh = TURN_FLOW.DIALOG_HEIGHT;

    this.dialogBorder.clear();
    this.dialogBorder.rect(cx - dw / 2, cy - dh / 2, dw, dh);
    this.dialogBorder.stroke({ color, width: TURN_FLOW.DIALOG_BORDER_WIDTH + 2 });

    this.dialogPlayerName.text = `PLAYER ${playerIndex + 1}`;
    this.dialogPlayerName.style.fill = color;

    this.dialogContainer.visible = true;
    this.dialogContainer.scale.set(TURN_FLOW.DIALOG_ENTER_SCALE);
    this.dialogContainer.alpha = 0;
    this.dialogShowTime = Date.now();
  }

  hide(): void {
    this.dialogContainer.visible = false;
  }

  update(state: GameState, dt: number): void {
    // Dialog entrance animation is handled by the container's scale/alpha
    // set in show() — PixiJS renders it. No per-frame logic needed beyond
    // what the parent HUD already does (calling hide at appropriate times).
  }

  destroy(): void {
    this.dialogBg.destroy();
    this.dialogBorder.destroy();
    this.dialogPlayerName.destroy();
    this.dialogPrompt.destroy();
    this.dialogInstruction.destroy();
  }
}
